import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { formatDate, titleCase } from '../../lib/format'
import type { CourseUpload } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import { GradCapIcon, PlusIcon } from '../../components/icons'

const FILTERS = [
  { value: 'pending_approval', label: 'Pending review' },
  { value: '', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'draft', label: 'Drafts' },
]

const REVIEW_ROLES = new Set(['super_admin', 'gov_director'])

export default function CourseUploads() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [status, setStatus] = useState('pending_approval')
  const [openId, setOpenId] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'course-uploads', status],
    queryFn: () => api.get<{ ok: boolean; items: CourseUpload[] }>(
      `/gov/course-uploads${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  })

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/gov/course-uploads/${id}/approve`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gov', 'course-uploads'] }); setOpenId(null) },
  })
  const reject = useMutation({
    mutationFn: ({ id, reason: r }: { id: string; reason: string }) =>
      api.post(`/gov/course-uploads/${id}/reject`, { reason: r }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gov', 'course-uploads'] }); setOpenId(null); setReason('') },
  })
  const submit = useMutation({
    mutationFn: (id: string) => api.post(`/gov/course-uploads/${id}/submit`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gov', 'course-uploads'] }) },
  })

  const items = data?.items ?? []
  const canReview = REVIEW_ROLES.has(user?.role ?? '')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Course uploads</h1>
          <p className="page-head__sub">Government-authored training courses awaiting review.</p>
        </div>
        <Link to="/course-uploads/new">
          <Button icon={<PlusIcon />}>New course draft</Button>
        </Link>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        {FILTERS.map((f) => (
          <Button key={f.label} size="sm" variant={status === f.value ? 'primary' : 'secondary'}
                  onClick={() => setStatus(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>

      {(approve.isError || reject.isError || submit.isError) && (
        <Alert tone="danger">
          {approve.error instanceof ApiError ? approve.error.message
            : reject.error instanceof ApiError ? reject.error.message
            : submit.error instanceof ApiError ? submit.error.message : 'That action failed.'}
        </Alert>
      )}

      <Card>
        <CardHeader title="Uploads" sub={items.length ? `${items.length} course${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }}><Skeleton h={20} /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<GradCapIcon size={22} />} title="Nothing here" body="" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Course</th><th>Submitted by</th><th>Created</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {items.map((c) => (
                    <Fragment key={c.id}>
                      <tr>
                        <td style={{ fontWeight: 600 }}>{c.course_name}</td>
                        <td>{c.created_by_name || '—'}</td>
                        <td>{formatDate(c.created_at)}</td>
                        <td><StatusBadge status={c.status} /></td>
                        <td className="table__num">
                          {(c.status === 'draft' || c.status === 'rejected') && c.created_by === user?.uid && c.id && (
                            <Button size="sm" loading={submit.isPending} onClick={() => submit.mutate(c.id!)}>
                              Submit for review
                            </Button>
                          )}
                          {canReview && c.status === 'pending_approval' && c.id && (
                            <Button size="sm" variant="secondary"
                                    onClick={() => { setOpenId(openId === c.id ? null : c.id!); setReason('') }}>
                              {openId === c.id ? 'Close' : 'Review'}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {openId === c.id && c.id && (
                        <tr>
                          <td colSpan={5} style={{ background: 'var(--bg-sunken)' }}>
                            <div className="stack" style={{ padding: 'var(--sp-2) 0' }}>
                              <p style={{ margin: 0 }}>{c.course_description}</p>
                              <div className="page-head__sub" style={{ margin: 0 }}>
                                {(c.modules ?? []).length} module{(c.modules ?? []).length === 1 ? '' : 's'} ·{' '}
                                {titleCase(c.level)} · {titleCase(c.target_role)}
                              </div>
                              <Textarea label="Rejection reason (required to reject)"
                                        value={reason} onChange={(e) => setReason(e.target.value)} />
                              <div className="row">
                                <Button size="sm" loading={approve.isPending} onClick={async () => {
                                  if (await confirm({ title: 'Approve this course?' })) approve.mutate(c.id!)
                                }}>
                                  Approve
                                </Button>
                                <Button size="sm" variant="danger" disabled={!reason.trim()} loading={reject.isPending}
                                        onClick={async () => {
                                          if (await confirm({ title: 'Reject this course?', message: reason, destructive: true })) {
                                            reject.mutate({ id: c.id!, reason })
                                          }
                                        }}>
                                  Reject
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
